/**
 * AN UPLOAD THAT REPORTS ITS PROGRESS AND GIVES UP ONLY WHEN THE BYTES ACTUALLY STOP MOVING.
 *
 * ⚠️ WHY XHR AND NOT `fetch`. `fetch()` cannot report UPLOAD progress on the clients this app runs
 * on — request streaming is Chromium-only and absent from iOS WebKit, which is most of the affected
 * population. `XMLHttpRequest.upload.onprogress` is the only mechanism that works everywhere here,
 * so the progress bar is real rather than a decorative animation over an opaque `fetch`.
 *
 * ⚠️ WHY A STALL DETECTOR AND NOT A WALL-CLOCK TIMEOUT — THIS IS THE LOAD-BEARING CHOICE.
 * The avatar path aborts an attempt after a fixed 10 s, which is right for a ~40 KB body. A report
 * attachment is up to 5 MB, and on the Iranian mobile links this platform serves an uplink of
 * ~200 kbps puts a 5 MB body at ~200 SECONDS of legitimate, healthy transfer. A 10 s — or any fixed —
 * deadline would cancel almost every upload that was about to succeed, turning the safeguard into
 * the outage. So there is NO wall-clock cap: an attempt is abandoned only when
 * {@link DEFAULT_STALL_MS} passes with ZERO bytes moved, which is what a severed connection actually
 * looks like. A slow link is allowed to be slow.
 *
 * The two features are the same mechanism: `upload.onprogress` drives the bar AND re-arms the stall
 * timer, so the bar cannot show motion that the detector has not seen.
 */

/** No bytes for this long ⇒ the connection is gone, not slow. */
export const DEFAULT_STALL_MS = 20_000;
/** Attempts per upload, mirroring the avatar path's bounded retry. */
export const DEFAULT_ATTEMPTS = 3;
/** Backoff between attempts — a fresh attempt is a genuinely later roll of the network. */
export const DEFAULT_BACKOFF_MS = [800, 2500] as const;

export interface UploadProgress {
  loaded: number;
  total: number;
  /** 0–1, or 0 when the total is unknown (`lengthComputable` false). */
  ratio: number;
}

export type UploadFailureKind = "stalled" | "network" | "http";

export class UploadError extends Error {
  constructor(
    readonly kind: UploadFailureKind,
    readonly status = 0,
  ) {
    super(`${kind}${status ? ` ${status}` : ""}`);
    this.name = "UploadError";
  }

  /**
   * A 4xx is the server's considered answer — the body is too large, the type is refused, the
   * session expired. Retrying it just repeats the same rejection over a bad link. Only transport
   * failures and 5xx get another attempt.
   */
  get retryable(): boolean {
    return this.kind === "http" ? this.status >= 500 : true;
  }
}

export interface UploadOptions {
  url: string;
  body: Blob;
  contentType: string;
  authorization?: string;
  onProgress?: (progress: UploadProgress) => void;
  stallMs?: number;
  attempts?: number;
  backoffMs?: readonly number[];
  /** Test seam. */
  createXhr?: () => XMLHttpRequest;
}

/** One attempt. Resolves with the response text on 2xx, else throws an {@link UploadError}. */
function attemptUpload(options: UploadOptions): Promise<string> {
  const stallMs = options.stallMs ?? DEFAULT_STALL_MS;
  const xhr = (options.createXhr ?? (() => new XMLHttpRequest()))();

  return new Promise<string>((resolve, reject) => {
    let stallTimer: ReturnType<typeof setTimeout> | undefined;
    let stalled = false;
    let settled = false;

    const clearStall = (): void => {
      if (stallTimer !== undefined) clearTimeout(stallTimer);
      stallTimer = undefined;
    };
    const armStall = (): void => {
      clearStall();
      stallTimer = setTimeout(() => {
        stalled = true;
        try {
          xhr.abort();
        } catch {
          // an abort that throws must not mask the stall we are reporting
        }
      }, stallMs);
    };
    const settle = (fn: () => void): void => {
      if (settled) return;
      settled = true;
      clearStall();
      fn();
    };

    xhr.upload.onprogress = (event: ProgressEvent): void => {
      armStall(); // bytes moved — the connection is alive, whatever its speed
      options.onProgress?.({
        loaded: event.loaded,
        total: event.total,
        ratio: event.lengthComputable && event.total > 0 ? event.loaded / event.total : 0,
      });
    };
    // The body is fully sent; the server is now thinking. Keep the stall guard armed so a
    // connection that dies while we wait for the response is still abandoned.
    xhr.upload.onload = (): void => armStall();
    xhr.onload = (): void =>
      settle(() => {
        if (xhr.status >= 200 && xhr.status < 300) resolve(xhr.responseText);
        else reject(new UploadError("http", xhr.status));
      });
    xhr.onerror = (): void => settle(() => reject(new UploadError("network")));
    xhr.onabort = (): void =>
      settle(() => reject(new UploadError(stalled ? "stalled" : "network")));

    try {
      xhr.open("POST", options.url, true);
      xhr.setRequestHeader("Content-Type", options.contentType);
      if (options.authorization) xhr.setRequestHeader("Authorization", options.authorization);
      armStall();
      xhr.send(options.body);
    } catch {
      settle(() => reject(new UploadError("network")));
    }
  });
}

const sleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Upload with bounded retries. Progress restarts from zero on each attempt — honestly, because the
 * bytes really do go again; a bar that pretended to resume would be lying about what is on the wire.
 */
export async function uploadWithProgress(options: UploadOptions): Promise<string> {
  const attempts = options.attempts ?? DEFAULT_ATTEMPTS;
  const backoff = options.backoffMs ?? DEFAULT_BACKOFF_MS;
  let last: unknown;
  for (let attempt = 0; attempt < attempts; attempt += 1) {
    try {
      return await attemptUpload(options);
    } catch (error) {
      last = error;
      if (error instanceof UploadError && !error.retryable) throw error;
      if (attempt === attempts - 1) break;
      await sleep(backoff[Math.min(attempt, backoff.length - 1)] ?? 0);
    }
  }
  throw last;
}
